import { useCallback, useRef, useState } from "react";

const STAGE_KEYS = ["build", "quotation", "order", "signing"];
const storageKey = (scope) => `mrc.placing.seen.${scope}`;

const read = (scope) => {
  try {
    return JSON.parse(window.localStorage.getItem(storageKey(scope)) || "null");
  } catch {
    return null;
  }
};
const write = (scope, value) => {
  try {
    window.localStorage.setItem(storageKey(scope), JSON.stringify(value));
  } catch {
    // Storage blocked (private window etc.): "new" just lasts for this visit
  }
};

/**
 * When the user last opened each placing stage on a portal page, kept in
 * this browser only (the API has no read/unread state). The first visit
 * starts from now, so nothing is "new" until something arrives.
 * Returns { seenAt, sinceLastVisit, markSeen(stage) }: seenAt moves when a
 * stage is opened (for the blocks' "new" badges); sinceLastVisit stays as
 * it was when the page opened (for the rows' NEW badges).
 */
export const useSeenStages = (scope) => {
  const [seenAt, setSeenAt] = useState(() => {
    const stored = read(scope);
    if (stored) return stored;
    const now = new Date().toISOString();
    const fresh = Object.fromEntries(STAGE_KEYS.map((k) => [k, now]));
    write(scope, fresh);
    return fresh;
  });
  const sinceLastVisit = useRef(seenAt).current;

  const markSeen = useCallback(
    (stage) =>
      setSeenAt((prev) => {
        const next = { ...prev, [stage]: new Date().toISOString() };
        write(scope, next);
        return next;
      }),
    [scope]
  );

  return { seenAt, sinceLastVisit, markSeen };
};

/** Whether something that happened at `date` is newer than the stage was last seen. */
export const isNewSince = (seen, stage, date) => {
  const at = date?.$date ?? date;
  return !!at && !!seen?.[stage] && new Date(at).getTime() > new Date(seen[stage]).getTime();
};
